import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Combobox as StyledCombobox } from "./Combobox";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

const CrownIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5ZM19 19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V18H19V19Z" />
  </svg>
);

/**
 * AssignmentOwnerSelector
 * 
 * A reusable component for selecting multiple owners and marking one as primary.
 * 
 * @param {Array} data - All available users/owners.
 * @param {Array} value - Selected owners in format [{ ownerId: string, isPrimary: boolean }].
 * @param {Function} onChange - Callback returning the updated value array.
 * @param {string} label - Label for the combobox.
 * @param {string} placeholder - Placeholder for the combobox.
 * @param {string} id - Unique ID for the combobox.
 * @param {string} displayField - Field to display in the list (default: 'fullName').
 * @param {string} idField - Field to use as ID (default: 'ownerId').
 */
export const AssignmentOwnerSelector = ({
  data = [],
  value = [],
  onChange,
  label,
  placeholder,
  id,
  displayField = 'fullName',
  idField = 'ownerId',
  searchFields = [],
  ...props
}) => {
  // Ensure value is an array
  const selectedOwners = Array.isArray(value) ? value : [];
  // Map the selected owners back to the full user objects from data for the Combobox
  const selectedValues = useMemo(() => {
    return selectedOwners
      .map(v => data.find(item => String(item[idField]) === String(v.ownerId || v.OwnerId)))
      .filter(Boolean);
  }, [selectedOwners, data, idField]);

  const primaryOwnerId = useMemo(() => {
    const primary = selectedOwners.find(v => v.isPrimary);
    return primary ? String(primary.ownerId || primary.OwnerId) : null;
  }, [selectedOwners]);

  const handleSelectionChange = (newSelectedUsers) => {
    const newOwnerIds = newSelectedUsers.map(user => String(user[idField]));
    
    // Create new value array, preserving isPrimary where possible
    let newValue = newOwnerIds.map(id => {
      const existing = selectedOwners.find(v => String(v.ownerId || v.OwnerId) === id);
      return {
        ownerId: id,
        isPrimary: existing ? existing.isPrimary : false
      };
    });

    // Ensure exactly one primary owner if any are selected
    if (newValue.length > 0) {
      const hasPrimary = newValue.some(v => v.isPrimary);
      if (!hasPrimary) {
        // If no primary, set the first one as primary
        newValue[0].isPrimary = true;
      }
    }

    onChange(newValue);
  };

  const setPrimary = (ownerId) => {
    let exists = false;
    const newValue = selectedOwners.map(v => {
      const currentId = String(v.ownerId || v.OwnerId);
      if (currentId === String(ownerId)) exists = true;
      return {
        ...v,
        ownerId: currentId,
        isPrimary: currentId === String(ownerId)
      };
    });

    if (!exists) {
      newValue.push({ ownerId: String(ownerId), isPrimary: true });
    }

    onChange(newValue);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getAvatar = (item) => {
    if (item.avatar) return item.avatar;
    return getInitials(item[displayField]);
  };

  return (
    <StyledCombobox
      id={id}
      label={label}
      placeholder={placeholder || "Select User"}
      multiple
      data={data}
      value={selectedValues}
      onChange={handleSelectionChange}
      displayField={displayField}
      searchFields={[displayField, 'roleName', 'designationName', ...searchFields]}
      renderOption={({ item, selected }) => {
        const itemId = String(item[idField]);
        const isPrimary = selected && primaryOwnerId === itemId;

        return (
          <div className="flex items-center justify-between w-full group">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-100 text-[10px] font-bold text-gray-600 dark:bg-dark-600 dark:text-dark-200">
                {getAvatar(item)}
              </div>
              <span className={clsx("text-sm", selected ? "font-bold" : "font-medium")}>
                {item[displayField]}
              </span>
              {Array.isArray(item.designations) && item.designations.length > 0 ? (() => {
                const maxVisible = 1;
                const visibleBadges = item.designations.slice(0, maxVisible);
                const extraDesignations = item.designations.slice(maxVisible);
                
                return (
                  <div className="ml-2 flex shrink-0 items-center justify-end gap-1">
                    {visibleBadges.map((badge, idx) => (
                      <span
                        key={badge.DesignationId || badge.designationId || idx}
                        title={badge.DesignationName || badge.designationName}
                        className="px-2 py-0.5 text-[9px] font-medium rounded-full bg-primary/10 text-primary border border-primary/20 dark:bg-primary-900/30 dark:text-primary-400 dark:border-primary-800/30 truncate max-w-[120px]"
                      >
                        {badge.DesignationName || badge.designationName}
                      </span>
                    ))}
                    {extraDesignations.length > 0 && (
                      <div className="relative inline-flex items-center">
                        <span 
                          data-tooltip-id={`badge-tooltip-owner-${item[idField]}`}
                          data-tooltip-html={extraDesignations.map(d => d.DesignationName || d.designationName).join("<br/>")}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} 
                          className="px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-gray-100 text-gray-700 border border-gray-200 dark:bg-dark-700 dark:text-dark-200 dark:border-dark-600 cursor-pointer hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                        >
                          +{extraDesignations.length}
                        </span>
                        <ReactTooltip 
                          id={`badge-tooltip-owner-${item[idField]}`}
                          place="right"
                          className="!z-[99999] !bg-gray-900/90 dark:!bg-gray-700/90 !backdrop-blur-sm !text-white !text-[10px] !px-2 !py-1 !rounded-md !shadow-lg"
                        />
                      </div>
                    )}
                  </div>
                );
              })() : (
                (item.designationName || item.roleName) && (
                  <span className="dark:bg-dark-700 ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] whitespace-nowrap text-gray-500 dark:text-gray-400">
                    {item.designationName || item.roleName}
                  </span>
                )
              )}
              {isPrimary && (
                <CrownIcon className="h-4 w-4 text-amber-400" />
              )}
            </div>

            {!isPrimary && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPrimary(itemId);
                }}
                className={clsx(
                  "opacity-0 group-hover:opacity-100 text-[12px] font-semibold px-2.5 py-1 rounded-md transition-all duration-200 cursor-pointer shadow-sm",
                  selected 
                    ? "bg-white text-primary-600 hover:bg-gray-50" 
                    : "bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-100"
                )}
              >
                Set Primary
              </button>
            )}
          </div>
        );
      }}
      inputProps={{
        onKeyDown: (e) => {
          if (e.key === 'Backspace' && e.currentTarget.value === '' && selectedValues.length > 0) {
            const nextValues = selectedValues.slice(0, -1);
            handleSelectionChange(nextValues);
          }
        }
      }}
      renderSelected={(selectedUsers) => (
        <div className="flex flex-wrap gap-1.5 items-center">
          {selectedUsers.map((user, index) => {
            const ownerId = String(user[idField]);
            const isPrimary = primaryOwnerId === ownerId;

            return (
              <React.Fragment key={ownerId}>
                {index > 0 && (
                  <span className="text-lg text-gray-350 dark:text-dark-500 font-light mx-1 select-none">
                    /
                  </span>
                )}
                <div
                  className={clsx(
                    "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-200 border",
                    isPrimary
                      ? "bg-primary-50/60 border-primary-200 text-primary-700 dark:bg-primary-950/20 dark:border-primary-900 dark:text-primary-400"
                      : "bg-gray-50/50 border-gray-150 text-gray-600 dark:bg-dark-800/40 dark:border-dark-700 dark:text-dark-300"
                  )}
                >
                  <div className={clsx(
                    "flex h-4.5 w-4.5 items-center justify-center rounded-md text-[8px] font-extrabold",
                    isPrimary 
                      ? "bg-primary-100 text-primary-700 dark:bg-primary-900/60 dark:text-primary-300" 
                      : "bg-gray-200/70 text-gray-500 dark:bg-dark-700/80"
                  )}>
                    {getAvatar(user)}
                  </div>
                  <span>{user[displayField]}</span>
                  {isPrimary && (
                    <CrownIcon className="h-3 w-3 text-amber-500 dark:text-amber-400 drop-shadow-sm" />
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
      {...props}
    />
  );
};
